/**
 * 阿里云 OSS 上传工具（与参考项目 AliUpload 一致，可直接使用 uploadCustomFile）
 * 支持项目内 getToken（如 this.$service.feedback.getUploadOssToken）或默认 axios 拉 STS
 */
// @ts-ignore
import OSS from 'ali-oss';
import axios, { AxiosResponse } from 'axios';

export interface STSResponse {
  SecurityToken: string;
  AccessKeyId: string;
  AccessKeySecret: string;
}

interface AliOSSConfig {
  tokenUrl: string;
  region: string;
  bucket: string;
  /** 可选：用项目接口拿 STS，不传则用 tokenUrl + axios 拉取 */
  getToken?: () => Promise<STSResponse | { data?: STSResponse }>;
}

const DEFAULT_OSS_CONFIG: AliOSSConfig = {
  tokenUrl: '/teaching/V2.0/api/token/getAliYunTokenForJzxFile',
  region: 'oss-cn-beijing',
  bucket: 'aixuexi-jzx-file',
};

class AliUpload {
  private static instance: AliUpload;

  private _client: OSS | null = null;

  private config: AliOSSConfig = DEFAULT_OSS_CONFIG;

  constructor(config: Partial<AliOSSConfig> = {}, isNew = false) {
    if (AliUpload.instance && !isNew) {
      return AliUpload.instance;
    }
    this.config = { ...DEFAULT_OSS_CONFIG, ...config };
    AliUpload.instance = this;
  }

  public static getInstance(config?: Partial<AliOSSConfig>, isNew = false): AliUpload {
    if (!AliUpload.instance || isNew) {
      AliUpload.instance = new AliUpload(config, isNew);
    }
    return AliUpload.instance;
  }

  public async init(): Promise<void> {
    if (this._client) return;

    let stsToken: STSResponse;
    try {
      stsToken = await this.getSTSToken();
    } catch (e) {
      throw new Error('获取 STS 失败');
    }
    this._client = new OSS({
      // @ts-ignore
      authorizationV4: true,
      secure: true,
      region: this.config.region,
      accessKeyId: stsToken.AccessKeyId,
      accessKeySecret: stsToken.AccessKeySecret,
      stsToken: stsToken.SecurityToken,
      bucket: this.config.bucket,
      refreshSTSToken: async () => {
        const newToken = await this.getSTSToken();
        return {
          accessKeyId: newToken.AccessKeyId,
          accessKeySecret: newToken.AccessKeySecret,
          stsToken: newToken.SecurityToken,
        };
      },
      refreshSTSTokenInterval: 5 * 60 * 1000,
    });
  }

  /** 统一拿 STS：优先 config.getToken，否则 axios.get(tokenUrl) */
  private async getSTSToken(): Promise<STSResponse> {
    if (this.config.getToken) {
      let raw: STSResponse | { data?: STSResponse };
      try {
        raw = await this.config.getToken();
      } catch (e) {
        // 项目 fetch 在 result.status !== 200 时会 reject(result)，而 getAliYunToken 返回的是
        // { SecurityToken, AccessKeyId, AccessKeySecret, StatusCode: '200' } 无 result.status，被误判为失败并 reject 出 STS
        const rejected = e as any;
        if (rejected && (rejected as any).SecurityToken && (rejected as any).AccessKeyId) {
          return rejected as STSResponse;
        }
        throw e;
      }
      if (raw == null || typeof raw !== 'object') {
        throw new Error('OSS STS 返回无效');
      }
      const token: STSResponse = (raw as any)?.data ?? (raw as STSResponse);
      if (!token || typeof token !== 'object' || !token.AccessKeyId || !token.AccessKeySecret || !token.SecurityToken) {
        throw new Error('OSS STS 凭证不完整');
      }
      return token;
    }
    const response: AxiosResponse<STSResponse> = await axios.get(this.config.tokenUrl, {
      withCredentials: false,
    });
    if (!response.data?.AccessKeyId || !response.data?.AccessKeySecret || !response.data?.SecurityToken) {
      throw new Error('STS凭证字段不完整');
    }
    return response.data;
  }

  private async ensureClientInitialized(): Promise<void> {
    if (!this._client) {
      await this.init();
    }
  }

  /**
   * 自定义文件上传（支持任意格式、自定义文件名）
   * @returns OSS 上传结果，result.url 即在线链接
   */
  public async uploadCustomFile(
    fileName: string,
    data: OSS.PutObjectData,
    mimeType?: string,
  ): Promise<OSS.PutResult> {
    await this.ensureClientInitialized();

    const uploadOptions: OSS.PutObjectOptions = {};
    if (mimeType) {
      uploadOptions.mime = mimeType;
      uploadOptions.headers = { 'Content-Type': mimeType };
    }

    let payload: Blob | OSS.PutObjectData = data;
    if (typeof data === 'string') {
      payload = new Blob([data], { type: mimeType || 'text/plain' });
    }

    try {
      const result = await this._client!.put(fileName, payload, uploadOptions);
      return result;
    } catch (putError) {
      const err = putError != null && typeof putError === 'object' ? (putError as any) : {};
      const code = err?.code ?? err?.name ?? 'Unknown';
      const message = err?.message ?? err?.requestId ?? String(putError);
      throw new Error(`OSS 上传失败: ${code} - ${message}`);
    }
  }

  public destroy(): void {
    this._client = null;
    AliUpload.instance = null as unknown as AliUpload;
  }
}

export default AliUpload;
